import * as audioOps from '../domain/audioOps';
import { INSTRUMENT_LABELS, SECTION_LABELS } from '../domain/factories';
import { OpError } from '../domain/projectOps';
import { STEPS_PER_BAR, type Project } from '../domain/schemas';
import type { Command } from './commands';

/** The explicit UI selection prompts act on. */
export interface PromptSelection {
  sampleId: string | null;
  clipId: string | null;
}

const fmt = (n: number) => String(Math.round(n * 100) / 100);

function selectedClip(project: Project, selection: PromptSelection | undefined) {
  if (!selection?.clipId) throw new OpError('No clip is selected. Select a clip on the Audio timeline first.');
  if (!project.audioClips.some((c) => c.id === selection.clipId)) throw new OpError('The selected clip no longer exists.');
  return audioOps.clipOf(project, selection.clipId);
}

function selectedSample(project: Project, selection: PromptSelection | undefined) {
  if (!selection?.sampleId) throw new OpError('No sample is selected. Select one in the Audio library first.');
  return audioOps.audioAsset(project, selection.sampleId);
}

export interface AudioCommandResult {
  project: Project;
  message: string;
  /** New selection after the command (e.g. the placed or duplicated clip). */
  selectClipId?: string | null;
}

/** Applies a sample/clip command. Throws OpError (nothing changes) on invalid targets. */
export function applyAudioCommand(
  project: Project,
  command: Command,
  selection: PromptSelection | undefined,
  newId: () => string,
): AudioCommandResult | null {
  switch (command.type) {
    case 'sample.assign': {
      const asset = selectedSample(project, selection);
      return {
        project: audioOps.assignSample(project, command.instrument, asset.id),
        message: `“${asset.name}” now plays on the ${INSTRUMENT_LABELS[command.instrument].toLowerCase()} track (its steps are unchanged).`,
      };
    }
    case 'sample.unassign':
      return {
        project: audioOps.unassignSample(project, command.instrument),
        message: `The ${INSTRUMENT_LABELS[command.instrument].toLowerCase()} track uses its built-in sound again.`,
      };
    case 'sample.place': {
      const asset = selectedSample(project, selection);
      let startStep = 0;
      let where = 'the start of the song';
      if (command.at.kind === 'section') {
        const kind = command.at.section;
        const section = project.arrangement.sections.find((s) => s.kind === kind);
        if (!section) throw new OpError(`There is no ${SECTION_LABELS[kind].toLowerCase()} section in the arrangement.`);
        startStep = audioOps.sectionStartStep(project, section.id);
        where = `the start of the ${section.name || SECTION_LABELS[kind].toLowerCase()} (bar ${startStep / STEPS_PER_BAR + 1})`;
      } else if (command.at.kind === 'bar') {
        startStep = (command.at.bar - 1) * STEPS_PER_BAR;
        where = `bar ${command.at.bar}`;
      }
      const id = newId();
      const next = audioOps.addClip(project, asset.id, { startStep, id });
      const lane = audioOps.clipOf(next, id).lane;
      return { project: next, message: `Placed “${asset.name}” at ${where} on lane ${lane + 1}.`, selectClipId: id };
    }
    case 'clip.reverse': {
      const clip = selectedClip(project, selection);
      return {
        project: audioOps.updateClip(project, clip.id, { reverse: command.reverse }),
        message: command.reverse ? `“${clip.name}” now plays reversed.` : `“${clip.name}” plays forwards.`,
      };
    }
    case 'clip.fade': {
      const clip = selectedClip(project, selection);
      const key = command.edge === 'in' ? 'fadeInSec' : 'fadeOutSec';
      const next = audioOps.updateClip(project, clip.id, { [key]: command.seconds });
      const applied = audioOps.clipOf(next, clip.id)[key];
      const clamped = applied + 1e-6 < command.seconds ? ` (limited to the clip length)` : '';
      return {
        project: next,
        message: applied === 0 ? `Removed the fade-${command.edge} from “${clip.name}”.` : `Fade-${command.edge} on “${clip.name}” set to ${fmt(applied)} s${clamped}.`,
      };
    }
    case 'clip.adjustGain':
    case 'clip.setGain': {
      const clip = selectedClip(project, selection);
      const target = command.type === 'clip.setGain' ? command.db : clip.gainDb + command.deltaDb;
      const next = audioOps.setClipGain(project, clip.id, target);
      return { project: next, message: `“${clip.name}” volume ${fmt(clip.gainDb)} → ${fmt(audioOps.clipOf(next, clip.id).gainDb)} dB.` };
    }
    case 'clip.duplicate': {
      const clip = selectedClip(project, selection);
      const id = newId();
      const next = audioOps.duplicateClip(project, clip.id, id);
      const copy = audioOps.clipOf(next, id);
      return { project: next, message: `Duplicated “${clip.name}” onto lane ${copy.lane + 1} right after the original.`, selectClipId: id };
    }
    case 'clip.mute': {
      const clip = selectedClip(project, selection);
      return { project: audioOps.updateClip(project, clip.id, { muted: command.muted }), message: `“${clip.name}” ${command.muted ? 'muted' : 'unmuted'}.` };
    }
    case 'clip.delete': {
      const clip = selectedClip(project, selection);
      return { project: audioOps.removeClip(project, clip.id), message: `Deleted the clip “${clip.name}” (the audio file stays in the library).`, selectClipId: null };
    }
    case 'clip.setRate': {
      const clip = selectedClip(project, selection);
      return {
        project: audioOps.updateClip(project, clip.id, { playbackRate: command.rate }),
        message: `“${clip.name}” plays at ${fmt(command.rate)}× (pitch changes with speed).`,
      };
    }
    case 'clip.normalize': {
      const clip = selectedClip(project, selection);
      return {
        project: audioOps.updateClip(project, clip.id, { normalize: command.normalize }),
        message: command.normalize ? `“${clip.name}” is normalised to −1 dBFS.` : `Normalisation removed from “${clip.name}”.`,
      };
    }
    case 'clip.loop': {
      const clip = selectedClip(project, selection);
      return {
        project: audioOps.updateClip(project, clip.id, { loops: command.loops }),
        message: command.loops === 1 ? `“${clip.name}” plays once.` : `“${clip.name}” repeats ${command.loops} times.`,
      };
    }
    default:
      return null;
  }
}
